import Description from "@mui/icons-material/Description";
import { ListItemIcon, ListItemText, MenuItem, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { useOpenNewlyNotReportingReports } from "hooks/useOpenNewlyNotReportingReports";
import pluralize from "pluralize";
import { useCallback, useMemo } from "react";

import { useBulkActionContext } from ".";

export function OpenNewlyNotReportingMenuItem() {
  const { setDialogState } = useBulkActionContext();

  return (
    <MenuItem onClick={() => setDialogState("openNewlyNotReporting")}>
      <ListItemIcon>
        <Description />
      </ListItemIcon>
      <ListItemText>Open Newly Not Reporting Reports</ListItemText>
    </MenuItem>
  );
}

export function OpenNewlyNotReportingDialog() {
  const { dialogState, onClose, selectedRows, reportingYear } = useBulkActionContext();
  const alerts = useAlerts();

  const [openNewlyNotReportingReports, { loading }] = useOpenNewlyNotReportingReports();

  const facilityIds = useMemo(
    () => [...new Set(selectedRows.map((row) => row.facilityId))],
    [selectedRows]
  );

  const handleSubmit = useCallback(async () => {
    if (facilityIds.length === 0) return;

    try {
      const result = await openNewlyNotReportingReports({
        variables: { facilityIds, reportingYear },
      });

      const createdCount = result.data?.openNewlyNotReportingReports.createdCount ?? 0;

      alerts.success(
        `Opened ${createdCount} newly not reporting ${pluralize("report", createdCount)}.`
      );
      onClose();
    } catch (error) {
      alerts.error("Error opening newly not reporting reports", error);
    }
  }, [facilityIds, reportingYear, openNewlyNotReportingReports, alerts, onClose]);

  if (dialogState !== "openNewlyNotReporting") {
    return null;
  }

  const canSubmit = facilityIds.length > 0;

  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={onClose}
      title="Open Newly Not Reporting Reports"
      primaryActionButtonProps={{
        onClick: handleSubmit,
        saveText: `Open ${facilityIds.length} ${pluralize("Report", facilityIds.length)}`,
        loading,
        disabled: !canSubmit,
      }}
    >
      {facilityIds.length > 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          You are about to open newly not reporting reports for{" "}
          <strong>
            {facilityIds.length} {pluralize("facility", facilityIds.length)}
          </strong>{" "}
          for reporting year {reportingYear}. Facilities do not need an existing report.
        </Typography>
      ) : (
        <Typography variant="body1" color="text.secondary">
          No facilities selected.
        </Typography>
      )}

      <DataGrid
        columns={[
          {
            field: "organization",
            headerName: "Organization",
            flex: 1,
            minWidth: 120,
          },
          {
            field: "facilityName",
            headerName: "Facility",
            flex: 1,
            minWidth: 120,
          },
          {
            field: "state",
            headerName: "State",
            width: 60,
          },
          {
            field: "reportKind",
            headerName: "Kind",
            width: 120,
          },
          {
            field: "organizationStatus",
            headerName: "Org Status",
            width: 140,
          },
          {
            field: "encampStatus",
            headerName: "Encamp Status",
            width: 150,
          },
        ]}
        rows={selectedRows}
        getRowId={(row) => row.id}
        rowHeight={36}
        disableRowSelectionOnClick
        hideFooter={selectedRows.length <= 10}
        sx={{ maxHeight: 300 }}
      />
    </Dialog>
  );
}
